import React, { memo, useId, useMemo } from "react";
import { marked } from "marked";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { normalizeMarkdownNewlines } from "@/shared/utils/markdownUtils";
import {
  ARTIFACT_URL_PREFIX,
  downloadArtifact,
} from "@/shared/utils/artifactDownload";

function normalizeDelimiters(text) {
  if (!text || typeof text !== "string") return text || "";
  return text
    .replace(/\uFF0A/g, "*")
    .replace(/\u2217/g, "*")
    .replace(/\u204E/g, "*")
    .replace(/\uFF3F/g, "_");
}

function parseMarkdownIntoBlocks(markdown) {
  if (!markdown) return [];
  try {
    return marked.lexer(markdown).map((token) => token.raw);
  } catch {
    return [markdown];
  }
}

const MARKDOWN_COMPONENTS = {
  a: ({ node: _node, href = "", children, ...props }) => {
    if (href.includes(ARTIFACT_URL_PREFIX)) {
      return (
        <button
          type="button"
          className="font-medium text-secondary underline"
          onClick={() =>
            downloadArtifact(href).catch((error) =>
              console.error("Artifact download failed:", error),
            )
          }
        >
          {children}
        </button>
      );
    }

    return (
      <a target="_blank" rel="noopener noreferrer" href={href} {...props}>
        {children}
      </a>
    );
  },
  table: ({ node: _node, children, ...props }) => (
    <div className="my-3 w-full overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="w-full min-w-max border-collapse text-left text-xs" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ node: _node, children, ...props }) => (
    <thead className="bg-gray-50 text-gray-700" {...props}>
      {children}
    </thead>
  ),
  tbody: ({ node: _node, children, ...props }) => (
    <tbody className="divide-y divide-gray-100" {...props}>
      {children}
    </tbody>
  ),
  th: ({ node: _node, children, ...props }) => (
    <th className="whitespace-nowrap px-3 py-2 font-semibold" {...props}>
      {children}
    </th>
  ),
  td: ({ node: _node, children, ...props }) => (
    <td className="max-w-[260px] whitespace-normal px-3 py-2 align-top text-gray-700" {...props}>
      {children}
    </td>
  ),
};

const MemoizedMarkdownBlock = memo(
  ({ content, components }) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  ),
  (prev, next) => prev.content === next.content,
);
MemoizedMarkdownBlock.displayName = "MemoizedMarkdownBlock";

const Markdown = memo(({ children, className = "", id, components }) => {
  const generatedId = useId();
  const blockId = id || generatedId;
  const source = useMemo(
    () => normalizeMarkdownNewlines(normalizeDelimiters(children)),
    [children],
  );
  const blocks = useMemo(() => parseMarkdownIntoBlocks(source), [source]);

  return (
    <div className={className}>
      {blocks.map((block, index) => (
        <MemoizedMarkdownBlock
          key={`${blockId}-block-${index}`}
          content={block}
          components={components || MARKDOWN_COMPONENTS}
        />
      ))}
    </div>
  );
});
Markdown.displayName = "Markdown";

const MessageContent = ({
  children,
  markdown = false,
  className = "",
  id,
  components,
}) => {
  if (markdown) {
    return (
      <Markdown id={id} className={className} components={components}>
        {typeof children === "string" ? children : String(children ?? "")}
      </Markdown>
    );
  }

  return (
    <div className={className} style={{ whiteSpace: "pre-wrap" }}>
      {children}
    </div>
  );
};

export default MessageContent;
export { Markdown };
